import { currentLanguage } from '@/i18n'

/**
 * An ISO date (`2026-09-10`) as a Date at local midnight.
 *
 * Built from the parts rather than `new Date(value)`, which parses a bare ISO date as **UTC**
 * midnight — west of Greenwich that formats back as the day before, and a fair would appear to
 * start a day early for anybody travelling.
 */
export function parseIsoDate(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) {
    return null
  }
  const [, year, month, day] = match
  const date = new Date(Number(year), Number(month) - 1, Number(day))
  // Rejects 2026-02-31, which the constructor would roll over into March.
  return date.getMonth() === Number(month) - 1 ? date : null
}

/**
 * A Date as an ISO date in the **local** calendar — `toISOString()` would answer in UTC, which
 * east of Greenwich turns an evening into the next day.
 */
export function toIsoDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

export function formatDate(value: string): string {
  const date = parseIsoDate(value)
  // The raw value beats "Invalid Date": if the backend ever sends something unexpected, showing it
  // is what makes that visible.
  return date ? formatter().format(date) : value
}

/**
 * "10.–13. Sep. 2026" — the shared year and month written once. `formatRange` does that itself and
 * knows how each language collapses a range; hand-rolling it produces "10. Sep. 2026 – 13. Sep.
 * 2026" in every one of them.
 */
export function formatPeriod(startDate: string, endDate: string): string {
  const start = parseIsoDate(startDate)
  const end = parseIsoDate(endDate)
  if (!start || !end) {
    return `${formatDate(startDate)} – ${formatDate(endDate)}`
  }
  return formatter().formatRange(start, end)
}

/**
 * "12. Sep." — a day without its year, for an axis where every tick shares one.
 *
 * The year is what makes a date label too wide to fit thirty of them side by side, and a chart
 * that spans at most a year has nothing to disambiguate with it.
 */
export function formatDayAndMonth(value: string): string {
  const date = parseIsoDate(value)
  if (!date) {
    return value
  }
  return new Intl.DateTimeFormat(currentLanguage(), { day: 'numeric', month: 'short' }).format(date)
}

/**
 * "10. Sep. 2026, 09:00" — an instant, for a line somebody wrote.
 *
 * With the time, unlike every other date in this application: a note is one entry in a running
 * record, and two notes written on the same day are told apart by the hour.
 */
export function formatDateTime(value: string): string {
  const moment = new Date(value)
  if (Number.isNaN(moment.getTime())) {
    // The raw value beats "Invalid Date": showing it is what makes an unexpected format visible.
    return value
  }
  return new Intl.DateTimeFormat(currentLanguage(), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(moment)
}

function formatter(): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat(currentLanguage(), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}
